import { useEffect, useId, useRef, type KeyboardEvent as ReactKeyboardEvent, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  acquireInert, escapeDecision, getOverlayRoot, handleTrapTab, initialFocusTarget, isTopOverlay,
  lockScroll, pushOverlay, removeOverlay, resolveVariant, type DialogVariant,
} from './overlayCore';
import { useIsDesktop, useLatest, usePresence } from './useOverlay';

/**
 * The overlay layer (T-27, PRD §4.6, DESIGN_SYSTEM.md §10.1): one accessible
 * dialog for every modal job.
 *
 * - `variant="center"` (default) becomes a bottom sheet below md;
 *   `variant="sheet"` is always a sheet; `variant="drawer"` slides from the
 *   inline-end edge.
 * - Portal to `#overlay-root`, `aria-modal`, focus trap, Escape (topmost only),
 *   return focus to the opener, scroll lock, `inert` on the app shell, enter
 *   AND exit motion (transitions on data-state), reduced motion = cross-fade.
 * - Solid `surface-raised` over an unblurred scrim (PRD §3.8); `material="float"`
 *   (the command palette) uses the float glass.
 *
 * `components/Modal.tsx` is a thin wrapper over this, so every existing caller
 * gets the same behaviour.
 */

export type DialogSize = 'sm' | 'md' | 'lg' | 'xl';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Visible title; also the accessible name. */
  title?: ReactNode;
  description?: ReactNode;
  /** Icon shown before the title. */
  icon?: ReactNode;
  variant?: DialogVariant;
  size?: DialogSize;
  /**
   * Extra panel classes (e.g. a max-width); replaces the size class when it sets `max-w-`.
   * Applies to every variant; `max-w-*` is ignored on sheets (always full width).
   */
  className?: string;
  /**
   * One fixed panel height for content that changes size (steppers, tabs): the
   * body scrolls and the footer stays put, so the dialog never jumps between
   * steps. Center: `h-dialog-stable`; sheet: `min-h-sheet-stable`.
   */
  stableHeight?: boolean;
  bodyClassName?: string;
  role?: 'dialog' | 'alertdialog';
  /** Element to focus on open. Default: the first field (desktop) or the panel (sheet). */
  initialFocus?: RefObject<HTMLElement | null>;
  /** Return focus to the opener on close (default true). */
  returnFocus?: boolean;
  /** Sticky footer (actions). */
  footer?: ReactNode;
  /** Escape and a click on the scrim close it (default true). */
  dismissible?: boolean;
  hideCloseButton?: boolean;
  /** Keep the title for screen readers only. */
  hideHeader?: boolean;
  /** Sheet height: medium ≈ half, large 92 dvh (PRD §4.4). */
  detent?: 'medium' | 'large';
  /** `center` turns into a sheet below md (default true). */
  sheetOnMobile?: boolean;
  /** `top` = command-palette placement (12 vh from the top). */
  position?: 'center' | 'top';
  material?: 'solid' | 'float';
  /** Accessible name when there is no visible title. */
  ariaLabel?: string;
  /** Test / feature hooks on the panel. */
  panelProps?: Record<`data-${string}`, string>;
}

const SIZE: Record<DialogSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
};

// Longest exit (sheet 220 ms) plus a margin; the layer unmounts after it.
const EXIT_MS = 260;

export default function Dialog({
  open,
  onClose,
  children,
  title,
  description,
  icon,
  variant: requested = 'center',
  size = 'md',
  className = '',
  stableHeight = false,
  bodyClassName,
  role = 'dialog',
  initialFocus,
  returnFocus = true,
  footer,
  dismissible = true,
  hideCloseButton = false,
  hideHeader = false,
  detent = 'large',
  sheetOnMobile = true,
  position = 'center',
  material = 'solid',
  ariaLabel,
  panelProps,
}: DialogProps) {
  const { t } = useTranslation();
  const isDesktop = useIsDesktop();
  const variant = resolveVariant(requested, isDesktop, sheetOnMobile);
  const { mounted, state } = usePresence(open, EXIT_MS);
  const id = useId();
  const titleId = `${id}-title`;
  const descId = `${id}-desc`;
  const panelRef = useRef<HTMLDivElement>(null);
  const scrimRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useLatest(onClose);
  const dismissibleRef = useLatest(dismissible);
  const initialFocusRef = useLatest(initialFocus);
  const variantRef = useLatest(variant);
  const returnFocusRef = useLatest(returnFocus);

  // Freeze the content while the exit transition runs, so a caller that clears
  // its data on close does not make the panel jump.
  const frozen = useRef({ children, title, description, footer, icon });
  if (open) frozen.current = { children, title, description, footer, icon };
  const content = open ? { children, title, description, footer, icon } : frozen.current;

  // Open: stack, scroll lock, inert shell, initial focus. Close: undo, return focus.
  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    pushOverlay(id);
    const unlock = lockScroll();
    const releaseInert = acquireInert();

    const raf = requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (!panel) return;
      // Respect an element the content already focused (autoFocus).
      if (panel.contains(document.activeElement) && document.activeElement !== panel) return;
      const explicit = initialFocusRef.current?.current ?? null;
      const policy = initialFocusTarget(variantRef.current, !!explicit);
      let target: HTMLElement | null = null;
      if (policy === 'explicit') target = explicit;
      else if (policy === 'first-field') {
        target = Array.from(panel.querySelectorAll<HTMLElement>('input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled])'))
          .find((el) => el.getClientRects().length > 0) ?? null;
      }
      (target ?? panel).focus({ preventScroll: true });
    });

    // Escape when focus is outside the panel (e.g. on <body> after a click on the scrim area).
    const onDocKey = (e: KeyboardEvent) => {
      const panel = panelRef.current;
      if (!panel || panel.contains(e.target as Node)) return;
      if (e.key === 'Escape' && escapeDecision({ isTop: isTopOverlay(id), dismissible: dismissibleRef.current, defaultPrevented: e.defaultPrevented }) === 'close') {
        e.preventDefault();
        onCloseRef.current();
      } else if (e.key === 'Tab' && isTopOverlay(id)) {
        handleTrapTab(panel, e);
      }
    };
    document.addEventListener('keydown', onDocKey);

    const panelEl = panelRef.current;
    const shouldReturnFocus = () => returnFocusRef.current;
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onDocKey);
      removeOverlay(id);
      unlock();
      releaseInert();
      const active = document.activeElement;
      const focusStillHere = !active || active === document.body || (panelEl?.contains(active) ?? false);
      if (shouldReturnFocus() && opener?.isConnected && focusStillHere) {
        opener.focus({ preventScroll: true });
      }
    };
  }, [open, id, onCloseRef, dismissibleRef, initialFocusRef, variantRef, returnFocusRef]);

  // Scrim click: only when the press started on the scrim too (no close on a drag out of a field).
  useEffect(() => {
    const scrim = scrimRef.current;
    if (!scrim || !open) return;
    let downOnScrim = false;
    const down = (e: PointerEvent) => { downOnScrim = e.target === scrim; };
    const click = (e: MouseEvent) => {
      if (downOnScrim && e.target === scrim && dismissibleRef.current) onCloseRef.current();
      downOnScrim = false;
    };
    scrim.addEventListener('pointerdown', down);
    scrim.addEventListener('click', click);
    return () => { scrim.removeEventListener('pointerdown', down); scrim.removeEventListener('click', click); };
  }, [open, mounted, onCloseRef, dismissibleRef]);

  if (!mounted || typeof document === 'undefined') return null;

  const onPanelKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      if (escapeDecision({ isTop: isTopOverlay(id), dismissible, defaultPrevented: e.defaultPrevented }) === 'close') {
        e.preventDefault();
        e.stopPropagation(); // page-level Escape listeners must not also fire
        onClose();
      }
      return;
    }
    if (e.key === 'Tab' && panelRef.current && isTopOverlay(id)) handleTrapTab(panelRef.current, e.nativeEvent);
  };

  const sized = /(^|\s)max-w-/.test(className) ? className : `${SIZE[size]} ${className}`;
  const layerLayout =
    variant === 'sheet' ? 'items-end justify-center'
      : variant === 'drawer' ? 'items-stretch justify-end'
        : position === 'top' ? 'items-start justify-center px-3 sm:px-4'
          : 'items-center justify-center p-4';
  const noMaxW = className.split(/\s+/).filter((c) => c && !/^max-w-/.test(c)).join(' ');
  // Drawers: 480 px by default, 672 px for `size="lg" | "xl"` (document viewers).
  const drawerW = size === 'lg' || size === 'xl' ? 'max-w-drawer-lg' : 'max-w-drawer';
  const panelLayout =
    variant === 'sheet' ? `w-full ${noMaxW} ${stableHeight ? 'min-h-sheet-stable' : ''}`
      : variant === 'drawer' ? `w-full ${drawerW} ${noMaxW}`
        : `w-full ${sized} ${stableHeight ? 'h-dialog-stable' : ''}`;

  const showHeader = !hideHeader && (content.title || !hideCloseButton);
  const labelledBy = content.title ? titleId : undefined;
  const sheetPad = { paddingBottom: 'calc(1.25rem + env(safe-area-inset-bottom))' };

  return createPortal(
    <div
      className={`ov-layer fixed inset-0 z-modal flex ${layerLayout}`}
      data-state={state}
      data-overlay-layer=""
      style={position === 'top' && variant === 'center' ? { paddingTop: '12vh' } : undefined}
    >
      <div ref={scrimRef} className="ov-scrim absolute inset-0" data-state={state} aria-hidden="true" />
      <div
        ref={panelRef}
        role={role}
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-label={labelledBy ? undefined : ariaLabel}
        aria-describedby={content.description ? descId : undefined}
        tabIndex={-1}
        onKeyDown={onPanelKeyDown}
        className={`ov-panel relative flex flex-col overflow-hidden focus:outline-none ${panelLayout}`}
        data-state={state}
        data-variant={variant}
        data-detent={variant === 'sheet' ? detent : undefined}
        data-position={position}
        data-material={material}
        {...panelProps}
      >
        {variant === 'sheet' && <div className="ov-grabber flex-shrink-0" aria-hidden="true" />}

        {hideHeader && content.title && <h2 id={titleId} className="sr-only">{content.title}</h2>}

        {showHeader && (
          <div className={`flex flex-shrink-0 items-start justify-between gap-4 px-5 ${variant === 'sheet' ? 'pt-2 pb-3' : 'pt-5 pb-4'}`}>
            <div className="min-w-0 flex-1">
              {content.title && (
                <h2 id={titleId} className="flex items-center gap-2 text-lg font-semibold text-text">
                  {content.icon}
                  <span className="min-w-0">{content.title}</span>
                </h2>
              )}
              {content.description && (
                <p id={descId} className="mt-1 text-sm text-text-2">{content.description}</p>
              )}
            </div>
            {!hideCloseButton && (
              <button
                type="button"
                onClick={onClose}
                aria-label={t('shell.close')}
                className="btn-icon -m-1.5 flex-shrink-0 text-text-3 hover:bg-surface-subtle hover:text-text transition-colors duration-micro"
              >
                <X size={18} strokeWidth={1.75} aria-hidden="true" />
              </button>
            )}
          </div>
        )}

        {!showHeader && !hideHeader && content.description && (
          <p id={descId} className="sr-only">{content.description}</p>
        )}

        <div
          className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${bodyClassName ?? (showHeader ? 'px-5 pb-5' : 'p-5')}`}
          style={variant === 'sheet' && !content.footer ? sheetPad : undefined}
          data-dialog-body=""
        >
          {content.children}
        </div>

        {content.footer && (
          <div
            className="flex flex-shrink-0 flex-wrap items-center justify-end gap-3 border-t border-border px-5 py-4"
            style={variant === 'sheet' ? { paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' } : undefined}
          >
            {content.footer}
          </div>
        )}
      </div>
    </div>,
    getOverlayRoot(),
  );
}
